import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

/**
 * The single declared surface for every environment variable this monorepo
 * reads. Nothing outside this file may touch `process.env`; the
 * `terac/no-process-env` lint rule enforces that, and the short exemption list
 * in that rule is the complete set of places where reading raw is unavoidable.
 *
 * Required (`.min(1)`, `.url()`) marks a variable the web build refuses to
 * start without. Everything else is `.optional()`: the same schema is loaded by
 * the Next.js app, the Trigger.dev workers, and the Slack bot, and a variable
 * that only one of those runtimes needs must not fail the others' boot. Call
 * sites already guard those (`if (!env.X) throw`), which is where the useful
 * error message lives.
 *
 * `devOptional()` is the third setting, for a variable that is genuinely
 * required in production but that no developer should have to hold locally: it
 * is required on the production deployment and `.optional()` everywhere else.
 */

/**
 * Validation is skipped where the process cannot have a complete environment:
 * lint / type-check / typegen runs, and Vitest. In those modes `env` is
 * `process.env` itself rather than a parsed snapshot, so tests that assign to
 * `process.env` between cases still observe their own values.
 */
const skipValidation =
  process.env.SKIP_ENV_VALIDATION === "true" ||
  process.env.NODE_ENV === "test" ||
  process.env.VITEST !== undefined ||
  process.env.npm_lifecycle_event === "lint" ||
  process.env.npm_lifecycle_event === "type-check" ||
  process.env.npm_lifecycle_event === "typegen";

/**
 * True only when a platform variable says this process is the production
 * deployment. `NODE_ENV` is deliberately not one of those signals: `next build`
 * and every Vercel preview set it to "production", so reading it here would
 * make a preview refuse to boot over a secret only production holds.
 *
 * A runtime that identifies itself as nothing counts as non-production. This
 * answer decides whether a missing variable stops a boot, and a process we
 * cannot place is not the one it should stop.
 *
 * Exported for callers that decide whether to RUN rather than whether to boot:
 * `seed-study-rates`, which writes rate rows and must never write them to the
 * live database, and `isPaidEnrichmentEnabled` in `@repo/tasks`, which keeps
 * previews and local runs from spending production's vendor credits.
 */
export function isProductionDeployment(): boolean {
  const triggerEnvironment = process.env.TRIGGER_ENVIRONMENT?.toLowerCase();
  if (triggerEnvironment) {
    return triggerEnvironment === "prod" || triggerEnvironment === "production";
  }
  if (process.env.VERCEL_ENV) {
    return process.env.VERCEL_ENV === "production";
  }
  return process.env.ENVIRONMENT === "production";
}

/**
 * Required on the production deployment, `.optional()` everywhere else.
 *
 * For a variable whose absence is a production incident but which no developer
 * should have to hold locally. The static type keeps `| undefined`, because in
 * dev the value really can be missing and call sites still have to guard; what
 * this buys is that production refuses to boot instead of throwing on the first
 * request that reaches the code path.
 *
 * The platform variable is read on every call rather than snapshotted at import,
 * so a test can set it and get either side of the branch.
 */
export function devOptional<T extends z.ZodTypeAny>(
  schema: T,
): T | z.ZodOptional<T> {
  return isProductionDeployment() ? schema : schema.optional();
}

/**
 * Exported for `__tests__/env.test.ts`, which has to build the schema itself:
 * under Vitest `env` below is the live `process.env` proxy, so it never
 * exercises the validated object's client/server access rules.
 */
export function createValidatedEnv() {
  return createEnv({
    server: {
      // Core
      DATABASE_URL: z.string().url(),
      // Read by the standalone agent database client in @repo/db; overrides
      // DATABASE_URL when the process should talk to the agent replica.
      AGENT_DATABASE_URL: z.string().url().optional(),
      DATABASE_CA: z.string().optional(),
      DRIZZLE_LOG: z.string().optional(),
      BETTER_AUTH_SECRET: z.string().min(1),
      BETTER_AUTH_URL: z.string().url().optional(),
      ENCRYPTION_SECRET: z.string().min(1),

      // SPL agent
      // Origin of the SPL agent (apps/spl-agent), a separate eve deployment
      // that /api/spl-agent proxies to. Optional because most environments
      // have no agent: unset is the off switch, and the proxy answers 503 so
      // the study panel says the agent is not available here instead of
      // hanging.
      SPL_AGENT_URL: z.string().url().optional(),
      // Shared secret the proxy sends and the agent requires, so a deployed
      // agent serves nobody but Terac. Optional here because a local agent
      // runs open; a deployment without it refuses every request.
      SPL_AGENT_INGRESS_SECRET: z.string().min(1).optional(),
      // Vercel Deployment Protection sits in front of the agent's preview
      // deployments: a custom domain is exempt on production but not on a
      // preview, so the proxy's server-side call is answered with a 401 and an
      // SSO redirect rather than by the agent. Sent as
      // `x-vercel-protection-bypass` so that call goes through. Optional: with
      // no protection in front of the agent, nothing needs bypassing.
      SPL_AGENT_PROTECTION_BYPASS: z.string().min(1).optional(),
      // Shared secret the SPL agent presents to mint a short-lived session for
      // the person talking to it. Optional, and unset is the off switch: the
      // agent asks whether the exchange is configured before using it, and a
      // Terac that mints nothing simply refuses.
      TERAC_AGENT_SECRET: z.string().min(1).optional(),
      // Vercel team and project the SPL agent deploys under, which the
      // sandboxes it creates inherit from its OIDC token. The Smartlead sandbox
      // proxy attaches Terac's key only for a sandbox whose token names both.
      // Optional so dev/CI passes; unset refuses every sandbox (fail-closed).
      VERCEL_TEAM_ID: z.string().min(1).optional(),
      VERCEL_SPL_AGENT_PROJECT_ID: z.string().min(1).optional(),
      // Neon control-plane credentials used to fork the analytics branch that
      // the SPL agent's query_database reads. The key can also delete
      // production, which is why it stays in the app runtime and never reaches
      // the agent. NEON_API_TOKEN is the older spelling some environments
      // still set; the project id falls back to a constant at the call site.
      NEON_API_KEY: z.string().min(1).optional(),
      NEON_API_TOKEN: z.string().min(1).optional(),
      NEON_PROJECT_ID: z.string().min(1).optional(),

      // AI
      AI_GATEWAY_API_KEY: z.string().min(1),
      GEMINI_API_KEY: z.string().min(1),
      OPENAI_API_KEY: z.string().min(1),
      // Gateway model id for assessment finding clustering; the call site
      // falls back to google/gemini-3.8-flash.
      ASSESSMENT_CLUSTER_MODEL: z.string().optional(),

      // Stripe
      STRIPE_SECRET_KEY: z.string().min(1),
      STRIPE_WEBHOOK_SECRET: z.string().min(1),
      STRIPE_IDENTITY_RESTRICTED_KEY: z.string().optional(),

      // Whop; external money rail for expert cashout. Optional so dev/CI
      // without creds passes env validation; getWhopConfig() throws at call
      // time and the whop-payouts-enabled flag stays off in unconfigured envs.
      WHOP_API_KEY: z.string().optional(),
      WHOP_PLATFORM_COMPANY_ID: z.string().optional(),
      WHOP_API_BASE_URL: z.string().url().optional(),
      // The stop switches of the cashout checker and the daily cashout audit:
      // "true" makes the schedule's next run return before it reads anything.
      // Trigger cannot pause a schedule declared in code.
      CASHOUT_CHECKER_STOPPED: z.string().optional(),
      CASHOUT_AUDIT_STOPPED: z.string().optional(),

      // OAuth providers
      GOOGLE_CLIENT_ID: z.string().min(1),
      GOOGLE_CLIENT_SECRET: z.string().min(1),
      IOS_GOOGLE_CLIENT_ID: z.string().optional(),
      ANDROID_GOOGLE_CLIENT_ID: z.string().optional(),
      APPLE_CLIENT_ID: z.string().optional(),
      APPLE_IOS_BUNDLE_ID: z.string().optional(),
      LINKEDIN_CLIENT_ID: z.string().min(1),
      LINKEDIN_CLIENT_SECRET: z.string().min(1),
      FACEBOOK_CLIENT_ID: z.string().min(1),
      FACEBOOK_CLIENT_SECRET: z.string().min(1),
      MICROSOFT_CLIENT_ID: z.string().min(1),
      MICROSOFT_CLIENT_SECRET: z.string().min(1),
      GITHUB_CLIENT_ID: z.string().min(1),
      GITHUB_CLIENT_SECRET: z.string().min(1),

      // Webhook secrets / integrations
      SLACK_SIGNING_SECRET: z.string().min(1),
      SLACK_BOT_TOKEN: z.string().min(1),
      SLACK_CLIENT_ID: z.string().min(1),
      SLACK_CLIENT_SECRET: z.string().min(1),
      SLACK_SPL_USERGROUP_ID: z.string().optional(),
      SLACK_SQL_AUDIT_WEBHOOK_URL: z.string().url().optional(),
      PERSONA_WEBHOOK_SECRET: z.string().min(1),
      PERSONA_API_KEY: z.string().min(1),
      PERSONA_TEMPLATE_ID: z.string().min(1),
      SMARTLEAD_WEBHOOK_SECRET: z.string().min(1),
      SMARTLEAD_API_KEY: z.string().min(1),
      WHOP_WEBHOOK_SECRET: z.string().optional(),
      // The `ws_` secret of the Whop transfer webhook. Only prod carries it; the
      // route rejects every delivery while it is unset.
      WHOP_TRANSFER_WEBHOOK_SECRET: z.string().optional(),
      // Signs the Trigger.dev alert webhook (the Alerts page shows it when the
      // webhook is created). The route rejects every delivery while it is unset.
      TRIGGER_ALERT_WEBHOOK_SECRET: z.string().optional(),
      // Svix signing secret for Resend email event webhooks. Optional so CI/build
      // without Infisical creds passes; the route rejects all requests if unset.
      RESEND_WEBHOOK_SECRET: z.string().optional(),
      RESEND_API_KEY: z.string().min(1),
      FORWARDEMAIL_API_KEY: z.string().min(1).optional(),
      EMAIL_FORWARDING_PROVIDER: z
        .enum(["forwardemail", "mailgun"])
        .default("forwardemail"),
      RUN_MAILGUN_LIVE_TESTS: z.string().optional(),
      MAILGUN_API_KEY: z.string().min(1).optional(),
      MAILGUN_REGION: z.enum(["US", "EU"]).default("US"),
      INTERCOM_FIN_SECRET: z.string().optional(),
      SUPPORT_AGENT_API_KEY: z.string().optional(),
      // Shared secret for the internal feasibility intake API (our own connectors
      // like the openclaw email pipeline). Optional so dev/CI passes; the routes
      // reject all requests when unset (fail-closed).
      FEASIBILITY_INTAKE_SECRET: z.string().optional(),
      // Svix secret for the Resend inbound-email webhook (distinct from the
      // outbound RESEND_WEBHOOK_SECRET). Optional so CI passes; fail-closed.
      RESEND_INBOUND_WEBHOOK_SECRET: z.string().optional(),
      // Svix secret for the expert-email inbound webhook. A third Resend secret
      // on purpose: it signs a different endpoint from the outbound events and
      // from feasibility intake, so revoking it stops expert capture without
      // touching either of the other two. Optional and unset until the
      // receiving domain has MX records; while it is absent the route answers
      // 503 and writes nothing.
      RESEND_EXPERT_INBOUND_WEBHOOK_SECRET: z.string().optional(),
      // Receiving domain for expert-email capture, e.g. "mail.terac.com".
      // Setting it turns capture on for every colleague at once; it is the only
      // gate, because the capture address is added to the SPL's own Reply-To
      // and never replaces it. Optional, and unset until that domain has MX
      // records: while it is absent `captureAddressFor` answers nothing and
      // outbound Reply-To headers stay exactly as they are today, so a
      // half-finished DNS change cannot point a reply at an address that
      // bounces.
      EXPERT_EMAIL_CAPTURE_DOMAIN: z.string().optional(),
      // Bearer token the Trigger.dev workers present to the web app's internal
      // webhook routes.
      WEBHOOK_SECRET: z.string().optional(),
      WEBHOOK_BASE_URL: z.string().url().optional(),

      // Linq; optional so dev/CI without creds passes env validation.
      // getLinqClient() throws if API_KEY is missing at call time, so the
      // PostHog cohort flag must stay off in environments without these.
      LINQ_API_KEY: z.string().optional(),
      LINQ_BASE_URL: z.string().url().optional(),
      LINQ_PHONE_NUMBER: z.string().optional(),
      LINQ_SIGNING_SECRET: z.string().optional(),
      // Linq escalation sync; optional so dev/CI without Linear credentials can
      // start. Production escalations retain Slack delivery if these are unset.
      // A personal API key stamped every issue with its owner and subscribed
      // them to it, so the whole escalation backlog read as one engineer's
      // work; the `actor=app` token minted from these carries the application.
      LINEAR_CLIENT_ID: z.string().min(1).optional(),
      LINEAR_CLIENT_SECRET: z.string().min(1).optional(),
      // Terac Bot (GitHub App); used by Slack slash commands to mutate
      // Repository Variables (deploy freeze state) via the GitHub REST API.
      TERAC_BOT_APP_ID: z.string().optional(),
      TERAC_BOT_PRIVATE_KEY: z.string().optional(),
      TERAC_BOT_INSTALLATION_ID: z.string().optional(),
      TERAC_BOT_REPO: z.string().optional(),

      // External APIs
      EXCHANGE_RATE_API_KEY: z.string().min(1),
      EXA_API_KEY: z.string().min(1),
      FIRECRAWL_API_KEY: z.string().min(1),
      // Credits held back from the Firecrawl monthly allowance; parsed with
      // Number() at the call site, which treats a malformed value as unset.
      FIRECRAWL_CREDIT_RESERVE: z.string().optional(),
      APOLLO_API_KEY: z.string().min(1),
      AGGKNOWLEDGE_API_KEY: z.string().min(1),
      CONTACTOUT_API_TOKEN: z.string().min(1),
      ASSEMBLY_AI_API_KEY: z.string().min(1),
      HEYGEN_API_KEY: z.string().min(1),
      NOTION_API_KEY: z.string().optional(),
      NYNE_API_KEY: z.string().min(1),
      NYNE_API_SECRET: z.string().min(1),
      GOOGLE_CUSTOM_SEARCH_API_KEY: z.string().min(1),
      GOOGLE_SEARCH_ENGINE_ID: z.string().min(1),

      // Optional: without it the live Google review rating is hidden.
      GOOGLE_PLACES_API_KEY: z.string().optional(),

      // Intercom Identity Verification (JWT signing key; "Unified Secret")
      INTERCOM_API_SECRET: z.string().optional(),

      // Status page (optional; falls back to "operational")
      BETTERSTACK_API_TOKEN: z.string().optional(),
      BETTERSTACK_STATUS_PAGE_ID: z.string().optional(),
      BETTERSTACK_TEST_STATUS: z
        .enum(["operational", "degraded", "downtime", "maintenance"])
        .optional(),

      // Applicant tracking / CRM connectors
      ASHBY_API_KEY: z.string().optional(),
      ASHBY_WEBHOOK_SECRET: z.string().optional(),
      ASHBY_DEPARTMENT_ID: z.string().optional(),
      ASHBY_INTERVIEW_PLAN_ID: z.string().optional(),
      ASHBY_JOB_TEMPLATE_ID: z.string().optional(),
      CRELATE_API_KEY: z.string().min(1),
      CRELATE_BASE_URL: z.string().url().optional(),
      // Monaco (sales CRM). Optional on purpose: the key IS the environment
      // gate, so staging and dev leave it unset and never write to the live
      // CRM sales outreaches from.
      MONACO_API_KEY: z.string().optional(),
      MONACO_BASE_URL: z.string().url().optional(),
      BOLDSIGN_API_KEY: z.string().min(1),
      BOLDSIGN_BASE_URL: z.string().url().optional(),
      BOLDSIGN_WEBHOOK_SECRET: z.string().optional(),

      // External recruitment panels
      USERINTERVIEWS_USE_SANDBOX: z.string().optional(),

      // Paid acquisition
      META_ADS_ACCESS_TOKEN: z.string().min(1),
      META_ADS_ACCOUNT_ID: z.string().min(1),
      META_PAGE_ID: z.string().min(1),
      MOCK_META_ADS: z.string().optional(),
      LINKEDIN_ADS_ACCOUNT_ID: z.string().min(1),
      LINKEDIN_ADS_REFRESH_TOKEN: z.string().min(1),
      LINKEDIN_ORGANIZATION_URN: z.string().min(1),

      // Outbound email
      OUTBOUND_ALLOW_NONPROD: z.string().optional(),
      // What the bounce guard does to a campaign that trips it: `alert` logs
      // the trip, `pause` also pauses the Smartlead campaign.
      OUTBOUND_BOUNCE_GUARD_MODE: z.enum(["alert", "pause"]).default("alert"),
      INVITATION_TEST_EMAIL: z.string().optional(),
      INVITATION_TEST_EMAILS: z.string().optional(),
      // Twilio backs phone verification (Verify) and line-type lookup only.
      // Outbound messaging was removed; text conversations run through Linq.
      TWILIO_ACCOUNT_SID: z.string().min(1),
      TWILIO_AUTH_TOKEN: z.string().min(1),
      TWILIO_VERIFY_SERVICE_SID: z.string().optional(),

      // Incentive rails
      VI_USER: z.string().optional(),
      VI_PASSWORD: z.string().optional(),

      // Time tracking (Insightful). `devOptional` because a developer has no
      // Insightful tenant to point at, while a production deployment missing
      // any of the four cannot provision an expert at all: InsightfulProvider's
      // constructor throws, and it throws on the first hourly study that needs
      // a tracker rather than at deploy.
      INSIGHTFUL_BASE_URL: devOptional(z.string().url()),
      INSIGHTFUL_API_TOKEN: devOptional(z.string()),
      // Both are mandatory on employee creation and neither is discoverable
      // from the token: the fixed `Experts` team and the shared settings
      // profile every employee uses.
      INSIGHTFUL_TEAM_ID: devOptional(z.string()),
      INSIGHTFUL_SHARED_SETTINGS_ID: devOptional(z.string()),

      // LiveKit
      LIVEKIT_URL: z.string().optional(),
      LIVEKIT_API_KEY: z.string().optional(),
      LIVEKIT_API_SECRET: z.string().optional(),
      LIVEKIT_AGENTS_NAME: z.string().optional(),

      // Identity face matching. Similarity thresholds and sample size, read as
      // numbers at the call site so they can be retuned without a deploy.
      IDENTITY_FACE_MATCH_THRESHOLD: z.string().optional(),
      IDENTITY_FACE_MISMATCH_THRESHOLD: z.string().optional(),
      IDENTITY_FACE_PROBE_SAMPLE_SIZE: z.string().optional(),

      // Infra / platform-injected
      VERCEL: z.string().optional(),
      VERCEL_ENV: z.enum(["production", "preview", "development"]).optional(),
      VERCEL_URL: z.string().optional(),
      VERCEL_GIT_COMMIT_SHA: z.string().optional(),
      VERCEL_GIT_COMMIT_REF: z.string().optional(),
      RENDER_INTERNAL_HOSTNAME: z.string().optional(),
      PORT: z.string().optional(),
      NEXT_RUNTIME: z.enum(["nodejs", "edge"]).optional(),
      NEXT_PHASE: z.string().optional(),
      // Injected by trigger.config.ts syncEnvVars; names the Trigger.dev
      // environment a worker is running in.
      TRIGGER_ENVIRONMENT: z.string().optional(),
      // Deployment tag used by the older Slack/broadcast guards, kept distinct
      // from VERCEL_ENV because Trigger workers set it and Vercel does not.
      ENVIRONMENT: z.string().optional(),
      SERVICE_NAME: z.string().optional(),
      OTEL_SERVICE_NAME: z.string().optional(),

      // AWS
      AWS_REGION: z.string().min(1).default("us-east-1"),
      AWS_ACCESS_KEY_ID: z.string().min(1),
      AWS_SECRET_ACCESS_KEY: z.string().min(1),
      AWS_S3_BUCKET_NAME: z.string().optional(),
      AWS_INTERVIEW_RECORDINGS_BUCKET: z.string().optional(),
      AWS_INTERVIEW_RECORDINGS_BUCKET_DEV: z.string().optional(),
      AWS_INTERVIEW_RECORDINGS_BUCKET_STAGING: z.string().optional(),
      AWS_INTERVIEW_STIMULI_BUCKET: z.string().optional(),
      AWS_INTERVIEW_STIMULI_BUCKET_DEV: z.string().optional(),
      AWS_INTERVIEW_STIMULI_BUCKET_STAGING: z.string().optional(),
      AWS_MEDIACONVERT_ROLE_ARN: z.string().min(1),
      MEDIACONVERT_SQS_QUEUE_URL: z.string().url(),

      // The knowledge store agent sandboxes mount. Optional as a set: an
      // environment without them serves agents whose knowledge tool refuses,
      // which is the intended state until the bucket exists there.
      AGENT_KNOWLEDGE_BUCKET: z.string().optional(),
      AGENT_KNOWLEDGE_ROLE_ARN: z.string().optional(),
      AGENT_KNOWLEDGE_ROLE_EXTERNAL_ID: z.string().optional(),

      // Upstash Redis (the store the legacy Vercel KV product was migrated to;
      // these are the variable names that actually exist in prod). Consumed via
      // the canonical getKvClient() factory in @repo/common/kv. The legacy
      // KV_REST_API_* names remain declared because the public-write rate
      // limiter still falls back to them.
      UPSTASH_REDIS_REST_URL: z.string().url().optional(),
      UPSTASH_REDIS_REST_TOKEN: z.string().optional(),
      KV_REST_API_URL: z.string().url().optional(),
      KV_REST_API_TOKEN: z.string().optional(),

      // PostHog server-side (feature flag reads, cohort sync)
      POSTHOG_PERSONAL_API_KEY: z.string().optional(),

      // SEO
      GOOGLE_SITE_VERIFICATION: z.string().optional(),

      // Test-only
      IDENTITY_FINGERPRINT_SECRET: z.string().optional(),
      VERCEL_AUTOMATION_BYPASS_SECRET: z.string().optional(),
      E2E_BASE_URL: z.string().url().optional(),
      CI: z.string().optional(),
      RUN_LLM_TESTS: z.string().optional(),
      // Opt-in for the database-backed specs (`*.db.test.ts`). They write rows,
      // so they refuse to run unless this is set alongside a DATABASE_URL that
      // points at a throwaway branch.
      RUN_DB_TESTS: z.string().optional(),
      // The second opt-in `seed-study-rates` needs, on top of RUN_DB_TESTS and
      // a DATABASE_URL. Two switches rather than one because that script fills
      // a study with weeks of rate history, and the gate it shares with the db
      // specs trusts the URL to be a throwaway branch rather than checking.
      SEED_STUDY_RATES: z.string().optional(),
      // The study `seed-study-rates` fills when no --study is passed.
      SEED_STUDY_ID: z.string().optional(),
    },
    client: {
      NEXT_PUBLIC_APP_URL: z.string().url().optional(),
      NEXT_PUBLIC_SITE_URL: z.string().url().optional(),
      NEXT_PUBLIC_BASE_URL: z.string().url().optional(),
      NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN: z.string().optional(),
      NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_STAGING: z.string().optional(),
      NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_DEV: z.string().optional(),
      NEXT_PUBLIC_MAPBOX_TOKEN: z.string().optional(),
      NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
      NEXT_PUBLIC_POSTHOG_HOST: z.string().url().optional(),
      NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
      // Which Whop environment the embedded payout portal talks to. Set to
      // "sandbox" in dev; defaults to production when unset.
      NEXT_PUBLIC_WHOP_ENVIRONMENT: z
        .enum(["production", "sandbox"])
        .optional(),
      NEXT_PUBLIC_GA_ID: z.string().optional(),
      NEXT_PUBLIC_GOOGLE_CLIENT_ID: z.string().optional(),
      NEXT_PUBLIC_INTERCOM_APP_ID: z.string().optional(),
      NEXT_PUBLIC_META_PIXEL_ID: z.string().optional(),
      // Datadog RUM. RUM stays inert until application id + client token are
      // set, so dev/CI without these passes. Version is inlined from the build
      // commit SHA (see next.config.ts `env`) to match the sourcemap upload tag.
      NEXT_PUBLIC_DD_RUM_APPLICATION_ID: z.string().optional(),
      NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN: z.string().optional(),
      NEXT_PUBLIC_DD_RUM_SITE: z.string().optional(),
      NEXT_PUBLIC_DD_RUM_VERSION: z.string().optional(),
      // Set to "1" locally to exercise RUM against a dev server. Never set in a
      // deployed environment: it is the only thing that lets a dev host report.
      NEXT_PUBLIC_DD_RUM_DEV: z.string().optional(),
      // Vercel platform env, auto-exposed to the client build. Used to gate
      // client-visible integrations (e.g. per-route GA4 tags) to production
      // only, so preview/dev traffic never reaches prod properties.
      NEXT_PUBLIC_VERCEL_ENV: z
        .enum(["production", "preview", "development"])
        .optional(),
      NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF: z.string().optional(),
    },
    shared: {
      NODE_ENV: z
        .enum(["development", "test", "production"])
        .default("development"),
    },
    // Next.js bundles NEXT_PUBLIC_* at build time; enumerate them here so the
    // values are available at runtime in the browser.
    experimental__runtimeEnv: {
      NODE_ENV: process.env.NODE_ENV,
      NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
      NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
      NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
      NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN:
        process.env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN,
      NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_STAGING:
        process.env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_STAGING,
      NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_DEV:
        process.env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_DEV,
      NEXT_PUBLIC_MAPBOX_TOKEN: process.env.NEXT_PUBLIC_MAPBOX_TOKEN,
      NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
      NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
        process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
      NEXT_PUBLIC_WHOP_ENVIRONMENT: process.env.NEXT_PUBLIC_WHOP_ENVIRONMENT,
      NEXT_PUBLIC_GA_ID: process.env.NEXT_PUBLIC_GA_ID,
      NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
      NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
      NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
      NEXT_PUBLIC_DD_RUM_APPLICATION_ID:
        process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
      NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN:
        process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
      NEXT_PUBLIC_DD_RUM_SITE: process.env.NEXT_PUBLIC_DD_RUM_SITE,
      NEXT_PUBLIC_DD_RUM_VERSION: process.env.NEXT_PUBLIC_DD_RUM_VERSION,
      NEXT_PUBLIC_DD_RUM_DEV: process.env.NEXT_PUBLIC_DD_RUM_DEV,
      NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
      NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF:
        process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF,
    },
    // A server variable read from client code has always been `undefined`:
    // Next.js only inlines NEXT_PUBLIC_*, so the value was never in the bundle
    // to begin with. t3-env's default is to throw instead, which turns that
    // latent no-op into an exception at module evaluation and blanks whatever
    // subtree imported the module: the RES-21 e2e table rendered its rows with
    // every cell empty. The typed signature says `never` because the default
    // throws; returning undefined is the documented way to opt out.
    //
    // This gives up t3-env's client/server guard, which was a runtime detector
    // for a build-time mistake. `import "server-only"` on the modules that hold
    // server credentials is the detector that belongs here, and is not done.
    onInvalidAccess: (() => undefined) as unknown as (
      variable: string,
    ) => never,
    // Treat empty strings as missing so `.optional()` works correctly.
    emptyStringAsUndefined: true,
    skipValidation: false,
  });
}

export type Env = ReturnType<typeof createValidatedEnv>;

/**
 * Reads through to whatever `process.env` currently is, rather than closing
 * over the object present at import time: suites that swap the whole object
 * (`process.env = { ...ORIGINAL }`) or stub a single key between cases have to
 * keep seeing their own values.
 */
function liveEnv(): Env {
  const raw = () => process.env as Record<string, string | undefined>;
  return new Proxy({} as Env, {
    get: (_target, key) => (typeof key === "string" ? raw()[key] : undefined),
    has: (_target, key) => typeof key === "string" && key in raw(),
  });
}

export const env: Env = skipValidation ? liveEnv() : createValidatedEnv();
